import { useTable } from "react-table";

import { classNames, PaginationType } from "helpers";
import { Pagination } from "components/Pagination";

export const DataTable = ({
  columns,
  data,
  pagination,
  onRowClick,
  nextClick,
  previousClick,
}: Props) => {
  const tableInstance = useTable({ columns, data });

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance;

  return (
    <div className="flex flex-col gap-3">
      <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
          <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
            <table
              {...getTableProps()}
              className="min-w-full divide-y divide-gray-200"
            >
              <thead className="bg-gray-50">
                {
                  // Loop over the header rows
                  headerGroups.map((headerGroup) => (
                    // Apply the header row props
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {
                        // Loop over the headers in each row
                        headerGroup.headers.map((column: any) => (
                          // Apply the header cell props
                          <th
                            {...column.getHeaderProps([
                              {
                                className: classNames(
                                  staticHeaderClass,
                                  column.headerClassName,
                                  column.onHeaderClick ? "cursor-pointer" : ""
                                ),
                                onClick: column.onHeaderClick,
                                scope: "col",
                              },
                            ])}
                          >
                            {
                              // Render the header
                              column.render("Header")
                            }
                          </th>
                        ))
                      }
                    </tr>
                  ))
                }
              </thead>
              {/* Apply the table body props */}
              <tbody {...getTableBodyProps()}>
                {
                  // Loop over the table rows
                  rows.map((row, index) => {
                    // Prepare the row for display
                    prepareRow(row);
                    return (
                      // Apply the row props
                      <tr
                        {...row.getRowProps()}
                        className={`cursor-pointer hover:bg-gray-100 ${
                          index % 2 === 0 ? "bg-white" : "bg-gray-50"
                        }`}
                        onClick={() =>
                          onRowClick ? onRowClick(row.original) : undefined
                        }
                      >
                        {
                          // Loop over the rows cells
                          row.cells.map((cell: any) => {
                            return (
                              <td
                                {...cell.getCellProps([
                                  {
                                    className: `${staticCellClass} ${cell.column.cellClassName}`,
                                    style: cell.column.style,
                                  },
                                ])}
                              >
                                {
                                  // Render the cell contents
                                  cell.render("Cell")
                                }
                              </td>
                            );
                          })
                        }
                      </tr>
                    );
                  })
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {pagination && (
        <Pagination
          totalRows={pagination.totalRows}
          currentPage={pagination.currentPage}
          pageSize={pagination.pageSize}
          nextClick={nextClick}
          previousClick={previousClick}
        />
      )}
    </div>
  );
};

interface Props {
  columns: any[];
  data: any[];
  pagination?: PaginationType;
  onRowClick?: (row: any) => void;
  previousClick?: () => void;
  nextClick?: () => void;
}

const staticCellClass =
  "px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900";

const staticHeaderClass =
  "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider";
